import React, { Children, Component } from 'react';
import styled, { css } from 'styled-components';
import { Input } from '..';
import { darken } from '../../utils/polished';
import { ClickAwayListener } from '../ClickAwayListener/ClickAwayListener';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import Portal from '../Portal/Portal';

interface IStyleProps {
  numChildren?: number;
  left?: number;
  top?: number;
  maxWidth?: number;
  fullWidth?: boolean;
}

const SearchElement = styled.div<IStyleProps>`
  max-width: ${(props: IStyleProps) => props.maxWidth && props.maxWidth + 'px'};
  ${props => {
    if (props.fullWidth) {
      return css`
        width: 100%;
      `;
    }
  }}
  position: relative;
  font-weight: 300;
  font-size: 16px;
  display: flex;
  align-items: center;
  flex: 1;
  .searchContainer {
    width: 100%;
    > div:first-child {
      margin: 0;
    }
  }
  button {
    position: absolute;
    right: 0;
  }
`;

const DropdownElement = styled.div`
  background-color: #1a1d1f;
  color: white;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  padding: 15px 20px;

  &&:hover {
    background-color: ${darken(0.1, '#647079')};
    cursor: pointer;
  }
`;
const DropdownFaceElement = styled.div`
  display: flex;
  align-items: center;
`;

const MenuLabelElement = styled.span`
  margin-right: 20px;
  white-space: nowrap;
`;

const MenuListElement = styled.ul`
  margin: 0;
  padding: 0;
  margin-top: 5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.3);
  max-height: 250px;
  min-width: 125px;
  border-radius: 5px;
  overflow-y: auto;
  position: absolute;
  left: ${(props: IStyleProps) => props.left || 0}px;
  top: ${(props: IStyleProps) => props.top || 0}px;
  z-index: 10;
  background-color: white;
  text-decoration: none;
`;

export interface IProps {
  /** Input ARIA label tag */
  ariaLabel?: string;
  /** The text that appears as the selected value for the dropdown. Defaults to undefined. */
  menuLabel?: string;
  /** Placeholder text for the search input.  Defaults to "Type here...". */
  placeholder?: string;
  /** Current value of the search input. Defaults to undefined. */
  value?: string;
  /** Max width of the search component.  Defaults to 600. */
  maxWidth?: number;
  /** Event fired whenever the search input changes. */
  onChange?: (value: string) => void;
  /** Event fired whenever the clear button is clicked. */
  onClearButtonClicked?: () => void;
  innerRef?: any;
  /** If true, the search bar will span the full width of its container. */
  fullWidth?: boolean;
}

export interface IState {
  showMenu: boolean;
  menuListLeftPosition: number;
  menuListTopPosition: number;
}

export class Search extends Component<IProps, IState> {
  public state: IState = {
    showMenu: false,
    menuListLeftPosition: 0,
    menuListTopPosition: 0,
  };
  private componentRef: any;

  public render() {
    const numChildren = Children.count(this.props.children);
    return (
      <SearchElement fullWidth={this.props.fullWidth} maxWidth={this.props.maxWidth}>
        {numChildren > 0 && (
          <DropdownElement ref={this.refComponent} onClick={this.handleMenuButtonClicked} data-testid="SearchDropdown">
            <DropdownFaceElement>
              <MenuLabelElement>{this.props.menuLabel}</MenuLabelElement>
              <Icon icon="chevron-down" size="extra-small" />
            </DropdownFaceElement>
            {this.state.showMenu && (
              <Portal>
                <ClickAwayListener onClickAway={this.handleClickAway}>
                  <MenuListElement
                    onClick={this.handleClickAway}
                    left={this.state.menuListLeftPosition}
                    top={this.state.menuListTopPosition}
                  >
                    {this.props.children}
                  </MenuListElement>
                </ClickAwayListener>
              </Portal>
            )}
          </DropdownElement>
        )}
        <Input
          ariaLabel={this.props.ariaLabel}
          ref={this.props.innerRef}
          onChange={this.handleInputChanged}
          placeholder={this.props.placeholder || 'Type here...'}
          inputTestId="SearchInput"
          className="searchContainer"
        />
        {this.props.value != null && this.props.value.length > 0 && (
          <IconButton
            data-testid="SearchClearButton"
            icon="times"
            onClick={this.props.onClearButtonClicked}
            ariaLabel="Clear search"
          />
        )}
      </SearchElement>
    );
  }

  public focus() {
    if (this.props.innerRef) {
      this.props.innerRef.focus();
    }
  }

  private handleMenuButtonClicked = e => {
    e.stopPropagation();
    this.setState({
      showMenu: !this.state.showMenu,
      menuListLeftPosition: this.getComponentLeftPosition(),
      menuListTopPosition: this.getComponentTopPosition() + 30,
    });
  };

  private handleClickAway = e => {
    this.setState({ showMenu: false });
  };

  private getComponentLeftPosition() {
    return this.componentRef.getBoundingClientRect().left;
  }

  private getComponentTopPosition() {
    return this.componentRef.getBoundingClientRect().top + document.documentElement.scrollTop;
  }

  private handleInputChanged = e => {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  private refComponent = (el: any) => (this.componentRef = el);
}
export default React.forwardRef((props, ref) => <Search innerRef={ref} {...props} />);
